import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { Mail, Phone, MapPin, Linkedin, Instagram, Twitter, Facebook } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const Footer = () => {
  const [services, setServices] = useState([]);
  const [socialMedia, setSocialMedia] = useState([]);
  const [settings, setSettings] = useState({});
  
  const socialIcons = {
    linkedin: Linkedin,
    instagram: Instagram,
    twitter: Twitter,
    facebook: Facebook
  };

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [servicesRes, socialRes, settingsRes] = await Promise.all([
        axios.get(`${BACKEND_URL}/api/cms/services`),
        axios.get(`${BACKEND_URL}/api/cms/social-media`),
        axios.get(`${BACKEND_URL}/api/cms/settings`)
      ]);
      setServices(servicesRes.data.slice(0, 5)); // Show first 5 services
      setSocialMedia(socialRes.data);
      setSettings(settingsRes.data);
    } catch (error) {
      console.error('Error fetching footer data:', error);
    }
  };

  return (
    <footer className="bg-black text-white py-16">
      <div className="max-w-7xl mx-auto px-4 sm:px-6">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-8 md:gap-12 mb-12">
          {/* Brand */}
          <div className="space-y-4">
            <h3 className="text-3xl font-light mb-4">{settings.site_name || 'Reagle.'}</h3>
            <p className="text-white/70 font-light leading-relaxed">
              Redefining real estate excellence through innovation and expertise.
            </p>
            
            {/* Social Media Links */}
            <div className="flex gap-4 pt-4">
              {socialMedia.map((social) => {
                const Icon = socialIcons[social.icon];
                return (
                  <a
                    key={social.id}
                    href={social.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="w-10 h-10 rounded-full bg-white/10 flex items-center justify-center hover:bg-[#0A1A50] transition-all duration-300 hover:scale-110 hover:-translate-y-1 group"
                    aria-label={social.name}
                  >
                    {Icon && <Icon className="w-5 h-5 text-white/70 group-hover:text-white transition-colors" />}
                  </a>
                );
              })}
            </div>
          </div>

          {/* Quick Links */}
          <div>
            <h4 className="text-lg font-normal mb-4">Quick Links</h4>
            <ul className="space-y-2">
              <li>
                <Link to="/" className="text-white/70 hover:text-[#0A1A50] transition-all duration-300 font-light hover:translate-x-1 inline-block">
                  Home
                </Link>
              </li>
              <li>
                <Link to="/about" className="text-white/70 hover:text-[#0A1A50] transition-all duration-300 font-light hover:translate-x-1 inline-block">
                  About Us
                </Link>
              </li>
              <li>
                <Link to="/blog" className="text-white/70 hover:text-[#0A1A50] transition-all duration-300 font-light hover:translate-x-1 inline-block">
                  Blogs
                </Link>
              </li>
              <li>
                <Link to="/contact" className="text-white/70 hover:text-[#0A1A50] transition-all duration-300 font-light hover:translate-x-1 inline-block">
                  Contact
                </Link>
              </li>
            </ul>
          </div>

          {/* Services */}
          <div>
            <h4 className="text-lg font-normal mb-4">Services</h4>
            <ul className="space-y-2">
              {services.map((service) => (
                <li key={service.id}>
                  <Link to={`/services/${service.service_id}`} className="text-white/70 hover:text-[#0A1A50] transition-all duration-300 font-light hover:translate-x-1 inline-block">
                    {service.title}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Contact */}
          <div>
            <h4 className="text-lg font-normal mb-4">Contact</h4>
            <ul className="space-y-3">
              <li className="flex items-start gap-3 text-white/70 hover:text-white transition-colors group">
                <Mail className="w-5 h-5 mt-0.5 flex-shrink-0 group-hover:scale-110 transition-transform" />
                <a href={`mailto:${settings.contact_email || ''}`} className="font-light hover:translate-x-1 inline-block transition-transform">
                  {settings.contact_email || 'info@reagle.ae'}
                </a>
              </li>
              <li className="flex items-start gap-3 text-white/70 hover:text-white transition-colors group">
                <Phone className="w-5 h-5 mt-0.5 flex-shrink-0 group-hover:scale-110 transition-transform" />
                <a href={`tel:${settings.contact_phone || ''}`} className="font-light hover:translate-x-1 inline-block transition-transform">
                  {settings.contact_phone || '+971 4 XXX XXXX'}
                </a>
              </li>
              <li className="flex items-start gap-3 text-white/70 hover:text-white transition-colors group">
                <MapPin className="w-5 h-5 mt-0.5 flex-shrink-0 group-hover:scale-110 transition-transform" />
                <span className="font-light">
                  {settings.contact_address || 'Dubai, United Arab Emirates'}
                </span>
              </li>
            </ul>
          </div>
        </div>

        {/* Bottom Bar */}
        <div className="border-t border-white/10 pt-8">
          <div className="flex flex-col md:flex-row justify-between items-center gap-4">
            <p className="text-white/50 text-sm font-light">
              © {new Date().getFullYear()} Reagle. All rights reserved.
            </p>
            <div className="flex gap-6">
              <Link to="/privacy" className="text-white/50 hover:text-[#0A1A50] text-sm transition-colors font-light">
                Privacy Policy
              </Link>
              <Link to="/terms" className="text-white/50 hover:text-[#0A1A50] text-sm transition-colors font-light">
                Terms of Service
              </Link>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;